import { type RecipeFormData } from '@/sections/asset/components/tabs/credentials/RecipeWizard/recipePayload';

export enum CredentialType {
  BASIC = 'basic',
  RECORDING = 'recording',
  BEARER_TOKEN = 'bearer_token',
  API_KEY = 'api_key',
  STATIC_TOKEN = 'static_token',
  RECIPE = 'recipe',
  COMPLEX_LOGIN = 'complex_login',
}

export enum RefreshIntervalUnit {
  SECONDS = 'seconds',
  MINUTES = 'minutes',
  HOURS = 'hours',
}

export interface RequestHeader {
  key: string;
  value: string;
}

// Separate form state interfaces for each credential type
export interface BasicCredentialFormData {
  label: string;
  username: string;
  password: string;
}

export interface RecordingCredentialFormData {
  label: string;
  file: File | null;
}

export interface DynamicConfigState {
  tokenServiceUrl: string;
  requestMethod: 'post' | 'get';
  requestHeaders: RequestHeader[];
  requestBody: string;
  refreshInterval: number;
  refreshIntervalUnit: RefreshIntervalUnit;
  tokenLocation: string;
}

export interface BearerTokenFormData {
  label: string;
  isDynamic: boolean;
  token: string;
  dynamicConfig: DynamicConfigState;
}

export interface ApiKeyFormData {
  label: string;
  parameterName: string;
  isDynamic: boolean;
  apiKey: string;
  addTo: 'header' | 'query' | 'cookie';
  dynamicConfig: DynamicConfigState;
}

export interface StaticTokenFormData {
  label: string;
  headers: Array<{ key: string; value: string }>;
}

// Complex Login spawns the recorder agent against the WebApplication.
// The agent never sees the literal values — only the keys are surfaced
// in its system prompt; values are substituted at sidecar-call time.
export interface ComplexLoginFormData {
  label: string;
  loginUrl: string;
  inputs: Array<{ key: string; value: string }>;
  hint: string;
}

// Initial state constants
export const BASIC_FORM_INITIAL: BasicCredentialFormData = {
  label: '',
  username: '',
  password: '',
};

export const RECORDING_FORM_INITIAL: RecordingCredentialFormData = {
  label: '',
  file: null,
};

export const DYNAMIC_CONFIG_INITIAL: DynamicConfigState = {
  tokenServiceUrl: '',
  requestMethod: 'post',
  requestHeaders: [],
  requestBody: '',
  refreshInterval: 60,
  refreshIntervalUnit: RefreshIntervalUnit.MINUTES,
  tokenLocation: '',
};

export const BEARER_TOKEN_FORM_INITIAL: BearerTokenFormData = {
  label: '',
  isDynamic: false,
  token: '',
  dynamicConfig: DYNAMIC_CONFIG_INITIAL,
};

export const API_KEY_FORM_INITIAL: ApiKeyFormData = {
  label: '',
  parameterName: '',
  isDynamic: false,
  apiKey: '',
  addTo: 'header',
  dynamicConfig: DYNAMIC_CONFIG_INITIAL,
};

export const STATIC_TOKEN_FORM_INITIAL: StaticTokenFormData = {
  label: '',
  headers: [{ key: '', value: '' }],
};

export const COMPLEX_LOGIN_FORM_INITIAL: ComplexLoginFormData = {
  label: '',
  loginUrl: '',
  inputs: [
    { key: 'username', value: '' },
    { key: 'password', value: '' },
  ],
  hint: '',
};

// Type-safe submit payload discriminated union
export type CredentialSubmitPayload =
  | { type: CredentialType.BASIC; data: BasicCredentialFormData }
  | {
      type: CredentialType.RECORDING;
      data: RecordingCredentialFormData & { file: File };
    }
  | { type: CredentialType.BEARER_TOKEN; data: BearerTokenFormData }
  | { type: CredentialType.API_KEY; data: ApiKeyFormData }
  | { type: CredentialType.STATIC_TOKEN; data: StaticTokenFormData }
  | { type: CredentialType.RECIPE; data: RecipeFormData }
  | { type: CredentialType.COMPLEX_LOGIN; data: ComplexLoginFormData };

// =============================================================================
// Multi-credential-set Complex Login (identities)
// =============================================================================

// Privilege titles vertical (privilege-escalation) authz tests. There is NO backend
// privilege field, so (LOCKED 2026-06-16) it is PERSISTED by encoding into the serialized
// credential label via a canonical marker — see complexLoginIdentities.ts.
// 'standard' carries NO marker (byte-identical to today); 'elevated' appends the suffix.
export type IdentityPrivilege = 'standard' | 'elevated';

// One credential SET / identity. `loginUrl` is now PER-IDENTITY (LOCKED 2026-06-16) —
// each identity logs in at its own URL (the backend blob's LoginURL is per-credential,
// webauth.go:44, and we send one blob per identity). `inputs` keeps the existing
// {key,value}[] shape (default seeded with username+password). `totpSecret` is the
// typed per-identity MFA field — canonical no-spaces base32 (or '' = none), the SAME
// canonical value TOTPSecretInput already emits.
export interface CredentialIdentity {
  id: string; // stable client id (crypto.randomUUID()) — list key + selection
  label: string;
  loginUrl: string; // PER-IDENTITY login page URL (validated by validateHttpUrl)
  privilege: IdentityPrivilege;
  // inputs carry a client-only `id` (crypto.randomUUID()) used ONLY as a stable
  // React list key in IdentityEditor; it is STRIPPED at the wire boundary
  // (identityToSingle) so ComplexLoginFormData.inputs stays {key,value}[].
  inputs: Array<{ id: string; key: string; value: string }>;
  totpSecret: string; // canonical base32, '' when no MFA
}

// The multi-set container. `loginUrl` is NO LONGER here (moved into CredentialIdentity).
// `hint` stays SHARED (app/page-level recorder guidance). `identities` is non-empty in a
// valid form.
export interface ComplexLoginMultiFormData {
  hint: string; // SHARED recorder guidance across identities
  identities: CredentialIdentity[];
}

// loginUrl is now seeded per-identity. The factory takes the web-app URL so each
// new identity defaults to the app's login page (operator can override per identity).
// Must be a factory (NOT a frozen const) so each call mints a fresh `id`.
export function makeEmptyIdentity(loginUrl = ''): CredentialIdentity {
  return {
    id: crypto.randomUUID(),
    label: '',
    loginUrl,
    privilege: 'standard',
    inputs: [
      { id: crypto.randomUUID(), key: 'username', value: '' },
      { id: crypto.randomUUID(), key: 'password', value: '' },
    ],
    totpSecret: '',
  };
}

// Seeds the first identity's loginUrl from the web-app URL (mirrors the current
// createCredentialBundle pre-fill of ComplexLoginFormData.loginUrl).
export function makeComplexLoginMultiInitial(
  loginUrl: string
): ComplexLoginMultiFormData {
  return { hint: '', identities: [makeEmptyIdentity(loginUrl)] };
}
